import * as React from 'react';
import styled from 'styled-components';

import { COLOR, FONT_SIZE, SPEED, Theme } from '../../packages/constants';

export interface GalleryIconProps {
    iconName: string;
    iconLabel: string;
    id: string;
    onChange: (e: any) => void;
    isSelected: boolean;
    assistAccess?: boolean;
    dataTestId?: string;
    imagePath?: string;
    onKeyDown?: (e: any) => void;
    themeProvider?: {};
    hasModernStyles?: boolean;
}

interface StyledIconProps {
    iconName: string;
    imagePath?: string;
    assistAccess?: boolean;
}

interface StyledLabelProps {
    dataTestId?: string;
}

interface StyledInputProps {
    assistAccess?: boolean;
    ref?: any;
    rel?: string;
    autoComplete?: string;
    hasModernStyles?: boolean;
    theme?: Theme;
}

interface DataTestIdProps {
    dataTestId: string;
}

const GalleryIconWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 100px;
    width: 33%;
    flex-basis: 33%;
`;

const Label = styled.label.attrs<DataTestIdProps>(({ dataTestId }) => ({
    'data-test-id': dataTestId,
}))``;

const StyledLabel = styled(Label)<StyledLabelProps>`
    width: 100px;
    height: 100px;
    display: block;
    position: relative;
    cursor: pointer;
`;

export const StyledIcon = styled.div<StyledIconProps>`
    box-sizing: border-box;
    margin-bottom: 12px;
    border: 3px solid ${COLOR.MAIN.ICON_BACKGROUND};
    border-radius: 15px;
    width: 100px;
    height: 100px;
    background-image: url(${(props) => `${props.imagePath ?? ''}${props.iconName}.svg`});
    background-color: ${COLOR.MAIN.ICON_BACKGROUND};
    background-repeat: no-repeat;
    background-position: center;
    background-size: 120% auto;
    box-shadow: 2px 2px 4px rgba(4, 37, 91, 0.15);
    transition: box-shadow ${SPEED.TRANSITION};
`;

const StyledLabelText = styled.div`
    box-sizing: border-box;
    position: relative;
    max-height: 80px;
    overflow: hidden;
    color: black;
    border-radius: 3px;
    font-size: ${FONT_SIZE.SMALL};
    font-family: sans-serif;
    line-height: ${FONT_SIZE.LARGE};
    padding: 5px 2px;
    text-align: center;
    background-color: ${COLOR.PRIMARY.WHITE};
    box-shadow: 0 0 15px rgba(4, 37, 91, 0);
`;

const StyledFadeDiv = styled.div`
    position: absolute;
    top: 60px;
    left: 0px;
    right: 0px;
    height: 20px;
    background: linear-gradient(0deg, #ffffff, rgba(255, 255, 255, 0));
`;

const StyledRadio = styled.input<StyledInputProps>`
    position: absolute;
    opacity: 0;
    top: 0;
    left: 0;
    z-index: -1;

    &:hover {
        ~ ${StyledIcon} {
            box-shadow: 0 0 15px rgba(4, 37, 91, 0.3);
        }
    }

    &:focus ~ ${StyledIcon} {
        outline: ${(props) => (props.assistAccess ? '1px dotted #212121' : '0')};
        outline: ${(props) => (props.assistAccess ? '5px auto -webkit-focus-ring-color' : '0')};
    }

    &:checked {
        ~ ${StyledIcon} {
            border-color: ${(props: StyledInputProps) =>
                props.hasModernStyles && props.theme?.mainBorder ? props.theme.mainBorder : COLOR.BRAND.REPORTING};
            box-shadow: 0 0 15px rgba(4, 37, 91, 0.3);
        }

        ${(props) =>
            !props.hasModernStyles &&
            `
            ~ ${StyledLabelText} {
                color: white;
                background-color: ${COLOR.BRAND.REPORTING};
                box-shadow: 0 0 15px rgba(4, 37, 91, 0.3);
            }
            
            ~ ${StyledLabelText} ${StyledFadeDiv} {
                background: linear-gradient(0deg, ${COLOR.BRAND.REPORTING}, rgba(255, 255, 255, 0));
            }
        `}
    }
`;

export const StyledGalleryIconWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    overflow: auto;
    padding: 15px 0 0 0;
    height: calc(100% - 95px);
    position: relative;
`;

export const GalleryIcon = React.forwardRef<HTMLInputElement, GalleryIconProps>(
    (
        {
            iconName,
            iconLabel,
            id,
            onChange,
            onKeyDown,
            assistAccess,
            isSelected,
            dataTestId,
            imagePath,
            hasModernStyles,
        },
        ref,
    ) => (
        <GalleryIconWrapper data-test-id={dataTestId}>
            <StyledLabel title={iconLabel} dataTestId={`${dataTestId}__label`}>
                <StyledRadio
                    data-test-id={`${dataTestId}__radio`}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    type={'radio'}
                    name={'gallery-icon'}
                    value={id}
                    ref={ref}
                    assistAccess={assistAccess}
                    defaultChecked={isSelected}
                    hasModernStyles={hasModernStyles}
                />
                <StyledIcon assistAccess={assistAccess} iconName={iconName} imagePath={imagePath} />
                <StyledLabelText>
                    {iconLabel}
                    <StyledFadeDiv />
                </StyledLabelText>
            </StyledLabel>
        </GalleryIconWrapper>
    ),
);
GalleryIcon.displayName = 'GalleryIcon';
